import { z } from 'zod';

import { type Nullable, resolveNullable } from '@/utils/nullable';

/**
 * Распознавание речи для конкретного аватара (`persons[].stt` в конфигурации).
 *
 * Новый бекенд задаёт его у аватара вместо общего `transcribe_url`.
 */
export interface SttConfigModel {
  readonly apiUrl: string | null;
  readonly model: string | null;

  /**
   * Ключ STT-сервиса. В открытом `GET /{tablet_slug}` его не бывает —
   * приходит только из `GET /api/v1/tablets/config` по токену планшета.
   * Кешируется внутри сохранённой конфигурации в localStorage — того же
   * уровня защиты, что pinHash (TODO: защищённое хранилище).
   */
  readonly apiKey: string | null;
}

/**
 * Приём для snake_case во всех моделях этого порта: схема объявляется
 * ключами ровно как их отдаёт бекенд, а `.transform()` переводит объект
 * в camelCase-модель. Где переименований нет — `.transform()` не нужен.
 *
 * `nullish()` вместо `nullable()`: в Dart `json['api_url'] as String?`
 * даёт `null` и когда ключа нет, и когда пришёл `null`.
 */
export const sttConfigModelSchema = z
  .object({
    api_url: z.string().nullish(),
    model: z.string().nullish(),
    api_key: z.string().nullish(),
  })
  .transform(
    (json): SttConfigModel => ({
      apiUrl: json.api_url ?? null,
      model: json.model ?? null,
      apiKey: json.api_key ?? null,
    }),
  );

export function sttConfigModelFromJson(json: unknown): SttConfigModel {
  return sttConfigModelSchema.parse(json);
}

export function sttConfigModelToJson(
  model: SttConfigModel,
): Record<string, unknown> {
  return {
    api_url: model.apiUrl,
    model: model.model,
    api_key: model.apiKey,
  };
}

/**
 * Патч для [sttConfigCopyWith]. Поле без обёртки не трогается,
 * `new Nullable(null)` обнуляет — ровно как sentinel `freezed` в Dart,
 * где явный `copyWith(apiKey: null)` действительно стирает ключ.
 */
export interface SttConfigModelPatch {
  readonly apiUrl?: Nullable<string | null>;
  readonly model?: Nullable<string | null>;
  readonly apiKey?: Nullable<string | null>;
}

/** `SttConfigModel.copyWith` — всегда новый объект, исходный не меняется. */
export function sttConfigCopyWith(
  model: SttConfigModel,
  patch: SttConfigModelPatch,
): SttConfigModel {
  return {
    apiUrl: resolveNullable(patch.apiUrl, model.apiUrl),
    model: resolveNullable(patch.model, model.model),
    apiKey: resolveNullable(patch.apiKey, model.apiKey),
  };
}
